import { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  WaterfallSectionsErf,
  WaterfallSections,
} from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import { Pwerm2WaterfallPartialTableProps } from '../pwerm-waterfall-utils/pwerm2-waterfall-shared';
import { useLocale } from '@app/core/hooks/useLocale';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import TableSectionValuesPerMultiple from '../../components/TableSectionValuesPerMultiple';
import {
  getWaterfallCaseYearMultipleValue,
  getWaterfallCaseYearValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

export const Pwerm2WaterfallNetExitProceedsPartialTable: FC<Pwerm2WaterfallPartialTableProps> = ({
  columnsCount,
}): JSX.Element => {
  const { l } = useLocale();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const { handleToggle } = useTogglerPwermWaterfall(WaterfallSectionsErf.NetExitProceeds);

  return (
    <>
      <tbody data-testid="net-exit-proceeds-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_NetExitProceedsTitle')}
          id={enumKeyByValue(WaterfallSections, WaterfallSections.NetExitProceeds)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.NetExitProceeds]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.NetExitProceeds],
        })}
        data-testid="net-exit-proceeds-section">
        <TableSectionValuesPerForecast
          title={l('_TransactionCosts')}
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              value={getCalculatedProjectValueInPercent(
                Number(
                  getWaterfallCaseYearValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    (year) => year.weightedTransactionCostsPc
                  )
                )
              )}
              {...enteredPercentageViewRules}
              dataTestId={`transaction-costs-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
        <TableSectionValuesPerForecast
          title={l('_IpoDiscount')}
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              value={getCalculatedProjectValueInPercent(
                Number(
                  getWaterfallCaseYearValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    (year) => year.weightedIpoDiscountPc
                  )
                )
              )}
              {...enteredPercentageViewRules}
              dataTestId={`ipo-discount-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
        <TableSectionValuesPerMultiple
          title={l('_NetExitProceeds')}
          renderCell={({ caseItem, forecast, multipleId }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearMultipleValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                multipleId!,
                (multiple) => multiple.netExitProceeds
              )}
              dataTestId={`net-exit-proceeds-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
            />
          )}
        />
        <TableSectionValuesPerForecast
          title={l('_WeightedNetExitProceeds')}
          additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          highlight
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.weightedNetExitProceeds
              )}
              {...amountRules}
              strong
              dataTestId={`weighted-net-exit-proceeds-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      </tbody>
    </>
  );
};
